import { Route, Routes } from "react-router";

import { AuthProvider } from "@/auth/auth-context";
import {
  RequireAdmin,
  RequireAnonymous,
  RequireApproved,
  RequireAuth,
} from "@/auth/route-guards";
import { AppShell } from "@/components/app-shell";
import { AdminUsersPage } from "@/routes/admin/users-page";
import { AwaitingApprovalPage } from "@/routes/awaiting-approval-page";
import { BirdDetailPage } from "@/routes/birds/bird-detail-page";
import { BirdPedigreePage } from "@/routes/birds/bird-pedigree-page";
import { BirdFormPage } from "@/routes/birds/bird-form-page";
import { BirdListPage } from "@/routes/birds/bird-list-page";
import { BreedingCalendarPage } from "@/routes/breeding/breeding-calendar-page";
import { NestDetailPage } from "@/routes/breeding/nest-detail-page";
import { PairRoundsPage } from "@/routes/breeding/pair-rounds-page";
import { PairsPage } from "@/routes/breeding/pairs-page";
import { SeasonsPage } from "@/routes/breeding/seasons-page";
import { HealthPage } from "@/routes/health/health-page";
import { LocationsPage } from "@/routes/settings/locations-page";
import { NotificationsPage } from "@/routes/settings/notifications-page";
import { ProfilePage } from "@/routes/settings/profile-page";
import { SecurityPage } from "@/routes/settings/security-page";
import { SettingsPage } from "@/routes/settings/settings-page";
import { StatisticsPage } from "@/routes/statistics/statistics-page";
import { DashboardPage } from "@/routes/dashboard-page";
import { LoginPage } from "@/routes/login-page";
import { ForgotPasswordPage, ResetPasswordPage } from "@/routes/password-pages";
import { RegisterPage } from "@/routes/register-page";

export function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Signed out only: following a stale bookmark while signed in lands in the app. */}
        <Route element={<RequireAnonymous />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        </Route>

        {/* Reachable either way: the breeder arrives here from an email link, and may
            well still have a live session on this device. */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        <Route element={<RequireAuth />}>
          {/* Signed in, but not yet let in. Outside the shell on purpose: a navigation
              bar whose every destination refuses them is worse than no bar. */}
          <Route path="/awaiting-approval" element={<AwaitingApprovalPage />} />

          <Route element={<RequireApproved />}>
            <Route element={<AppShell />}>
              <Route path="/" element={<DashboardPage />} />

              {/* "new" before ":id" — otherwise /birds/new resolves as a bird whose id
                  is the literal string "new" and the detail screen 404s. */}
              <Route path="/birds" element={<BirdListPage />} />
              <Route path="/birds/new" element={<BirdFormPage />} />
              <Route path="/birds/:id" element={<BirdDetailPage />} />
              <Route path="/birds/:id/pedigree" element={<BirdPedigreePage />} />
              <Route path="/birds/:id/edit" element={<BirdFormPage />} />

              {/* "calendar" before ":id" — otherwise it resolves as a pair whose id is
                  the literal string "calendar". */}
              <Route path="/breeding" element={<PairsPage />} />
              <Route path="/breeding/calendar" element={<BreedingCalendarPage />} />
              <Route path="/breeding/seasons" element={<SeasonsPage />} />
              <Route path="/breeding/pairs/:id" element={<PairRoundsPage />} />
              <Route path="/breeding/nests/:id" element={<NestDetailPage />} />

              <Route path="/health" element={<HealthPage />} />
              <Route path="/statistics" element={<StatisticsPage />} />
              <Route path="/more" element={<SettingsPage />} />
              <Route path="/more/locations" element={<LocationsPage />} />
              <Route path="/more/notifications" element={<NotificationsPage />} />
              <Route path="/more/profile" element={<ProfilePage />} />
              <Route path="/more/security" element={<SecurityPage />} />

              {/* Hidden from anyone without the role, and refused by the API besides. */}
              <Route element={<RequireAdmin />}>
                <Route path="/admin/users" element={<AdminUsersPage />} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Routes>
    </AuthProvider>
  );
}
